/**
 * OriginsSettings — origens.
 * Mobile: toolbar Contatos-like. Desktop intacto.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { useOriginsPage } from './hooks/useOriginsPage';
import OriginTable from './components/OriginTable';
import OriginFormModal from './components/OriginFormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import ActionMenu from '../../../components/shared/ActionMenu';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import BulkActionsBar from '../../../components/shared/BulkActionsBar';
import Pagination from '../../../components/shared/Pagination';

const OriginsSettings: React.FC = () => {
    const {
        filteredOrigins,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        originToEdit,
        originToDelete,
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveOrigin,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions,
    } = useOriginsPage();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedOrigins = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        return filteredOrigins.slice(firstPageIndex, firstPageIndex + pageSize);
    }, [currentPage, filteredOrigins, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Origens"
                subtitle="Cadastre as origens de onde vêm seus contatos."
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                actionClassName="w-full md:w-auto"
                hideBackButton
                action={
                    <>
                        <div className="flex w-full flex-col gap-s3 md:hidden">
                            <div
                                role="toolbar"
                                aria-label="Ações de origens"
                                className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card"
                            >
                                <ActionMenu
                                    actions={headerActions}
                                    buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                                />
                            </div>
                            <SearchInput
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Pesquisar origens..."
                                className="min-h-11 w-full"
                            />
                            <Button
                                type="button"
                                onClick={handleOpenCreateModal}
                                className="min-h-11 w-full touch-manipulation"
                            >
                                Nova Origem
                            </Button>
                        </div>
                        <div className="hidden md:flex md:items-center md:gap-2.5">
                            <div className="w-[250px] shrink-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar origens..."
                                    className="h-[36px]"
                                />
                            </div>
                            <Button type="button" onClick={handleOpenCreateModal}>
                                Nova Origem
                            </Button>
                            <ActionMenu actions={headerActions} />
                        </div>
                    </>
                }
            />

            <section aria-label="Lista de origens" className="overflow-x-auto pt-s3 md:pt-0">
                <OriginTable
                    origins={paginatedOrigins}
                    isLoading={loading}
                    onEdit={handleOpenEditModal}
                    onDelete={handleOpenDeleteModal}
                    onDuplicate={handleDuplicate}
                    selectedIds={selectedIds}
                    onToggleSelection={toggleSelection}
                    onToggleAll={() => toggleAllSelection(filteredOrigins)}
                />
            </section>
            <Pagination
                className="mt-s3"
                currentPage={currentPage}
                totalCount={filteredOrigins.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                selectedCount={selectedIds.size}
            />

            <OriginFormModal
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSaveOrigin}
                originToEdit={originToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Excluir Origem"
                message={`Tem certeza que deseja excluir a origem "${originToDelete?.name}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Origens"
                message={`Tem certeza que deseja excluir ${selectedIds.size} origem(ns)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />
        </div>
    );
};

export default OriginsSettings;
